import { AppLink } from '@/shared/components/AppLink';
import type { HeroPresentationVm } from '../viewModels/newHome.vm';

interface HeroVideoSectionProps {
  data: HeroPresentationVm;
  eventLabel: string;
}

const DEFAULT_HERO_HEIGHT = '70vh';
const DEFAULT_HERO_MAX_HEIGHT = 'min(88vh, 920px)';

export default function HeroVideoSection({ data, eventLabel }: HeroVideoSectionProps) {
  // Fades the video's bottom edge into the gradientColor that starts the post-hero wrapper
  const bottomGradient = data.gradientColor
    ? `linear-gradient(to bottom, transparent 45%, ${data.gradientColor} 100%)`
    : undefined;

  const heroMediaHeight = data.height?.trim() || DEFAULT_HERO_HEIGHT;
  const heroMediaMaxHeight = data.maxHeight?.trim() || DEFAULT_HERO_MAX_HEIGHT;

  const heroMediaBoxStyle = {
    height: heroMediaHeight,
    maxHeight: heroMediaMaxHeight,
  } as const;

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: data.bgColor }}
      data-analytics-section-name={eventLabel}
    >
      <AppLink to={data.link} shouldRefresh={false} eventName="Hero Banner Clicked">
        {data.isVideo ? (
          <div
            className="relative w-full min-h-0 overflow-hidden"
            style={heroMediaBoxStyle}
          >
            <video
              className="h-full max-h-full min-h-0 w-full object-cover object-center"
              poster={data.posterUrl}
              autoPlay={!!data.autoplay}
              playsInline={!!data.playsinline}
              loop={!!data.loop}
              muted={!!data.muted}
              preload="auto"
            >
              <source src={data.bgUrl} type="video/mp4" />
            </video>

            {data.isGradient && data.fwImageUrl && (
              <div className="absolute inset-0 flex items-center">
                {data.fwImageBgUrl && (
                  <img
                    src={data.fwImageBgUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    aria-hidden="true"
                  />
                )}
                <img
                  src={data.fwImageUrl}
                  alt=""
                  className="relative z-10"
                  style={{
                    width: data.fwImageWidth ?? '50%',
                    ...(data.fwImagePosition === 'left' ? { marginLeft: '1rem' } : {}),
                    ...(data.fwImagePosition === 'right' ? { marginLeft: 'auto', marginRight: '1rem' } : {}),
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className="relative w-full min-h-0 overflow-hidden"
            style={heroMediaBoxStyle}
          >
            <img
              src={data.bgUrl}
              alt={eventLabel}
              className="h-full max-h-full min-h-0 w-full object-cover object-center"
            />
          </div>
        )}
      </AppLink>

      {/* Bottom fade: blends from the video's dominant color into the page white */}
      {bottomGradient && (
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 md:h-48"
          style={{ background: bottomGradient }}
          aria-hidden
        />
      )}
    </section>
  );
}
